import { useLayoutEffect, useState } from "react";

import classes from "./useScrollingTabRow.module.css";

/**
 * Scrolls only the tab row, by the least amount that shows the whole tab clear of the faded right
 * edge. `scrollIntoView` would also scroll the page to bring the row itself into view.
 */
function scrollTabIntoRow(row: HTMLElement, tab: HTMLElement) {
  const fadeWidth = Number.parseFloat(getComputedStyle(row).scrollPaddingInlineEnd) || 0;
  const rowRect = row.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();
  const tabStart = tabRect.left - rowRect.left + row.scrollLeft;
  const tabEnd = tabStart + tabRect.width;
  const visibleEnd = row.scrollLeft + row.clientWidth - fadeWidth;

  if (tabStart < row.scrollLeft) {
    row.scrollTo({ left: tabStart });
  } else if (tabEnd > visibleEnd) {
    row.scrollTo({ left: tabEnd - row.clientWidth + fadeWidth });
  }
}

/**
 * Marks the row while its tabs overflow it, so only then does it fade its right edge. The row's
 * end padding is left out of the measure: the mark itself widens that padding, and counting it
 * would keep a row marked after its tabs fit again.
 */
function markOverflow(row: HTMLElement) {
  const endPadding = Number.parseFloat(getComputedStyle(row).paddingInlineEnd) || 0;
  row.toggleAttribute("data-overflowing", row.scrollWidth > row.clientWidth + endPadding);
}

/** Re-marks the overflow and keeps the active tab in view. */
function settleRow(row: HTMLElement) {
  markOverflow(row);
  const tab = row.querySelector<HTMLElement>("[data-active]");
  if (tab) {
    scrollTabIntoRow(row, tab);
  }
}

/**
 * A `Tabs.List` that scrolls sideways in one line on phones; `ScrollingTabsList` wraps it. Give
 * the list the returned `ref` and `className`. The active tab is scrolled into view when the list
 * mounts and whenever another tab becomes active, so deep links never land on a tab past the
 * right edge. Where the tabs fit, nothing scrolls or fades.
 */
export function useScrollingTabRow() {
  const [row, setRow] = useState<HTMLDivElement | null>(null);

  // Before paint, so a tab that lands past the edge never flashes there first.
  useLayoutEffect(() => {
    if (!row) return;
    settleRow(row);
    // The border box: marking the row changes its padding, which must not count as a resize.
    const resizeObserver = new ResizeObserver(() => markOverflow(row));
    resizeObserver.observe(row, { box: "border-box" });
    // Tabs added, removed, relabelled or made active change the row without resizing it.
    const mutationObserver = new MutationObserver(() => settleRow(row));
    mutationObserver.observe(row, {
      subtree: true,
      childList: true,
      characterData: true,
      attributeFilter: ["data-active"],
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [row]);

  return { ref: setRow, className: classes.row };
}
